import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { ReactNode, createContext, useContext, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';

import { UsersService } from '@/client';
import Loader from '@/components/Loader';

export enum UserRoleEnum {
  SUPER_ADMIN = 'super_admin',
  CONSULTANT = 'consultant',
  BASE_USER = 'base_user',
  ADMIN = 'admin',
  EDITOR = 'editor',
  COLLECTOR = 'collector',
  NOTUSR = 'notusr',
}

export const UserRoleHierarchy: Record<UserRoleEnum, number> = {
  [UserRoleEnum.SUPER_ADMIN]: 6,
  [UserRoleEnum.CONSULTANT]: 5,
  [UserRoleEnum.ADMIN]: 4,
  [UserRoleEnum.EDITOR]: 3,
  [UserRoleEnum.COLLECTOR]: 2,
  [UserRoleEnum.BASE_USER]: 1,
  [UserRoleEnum.NOTUSR]: 0,
};

export const isRoleGreaterOrEqual = (roleA: UserRoleEnum | null, roleB: UserRoleEnum): boolean => {
  if (!roleA) return false;
  return UserRoleHierarchy[roleA] >= UserRoleHierarchy[roleB];
};

interface UserRoleContextType {
  userRole: UserRoleEnum | null;
  isAuthenticated: boolean;
}

const UserRoleContext = createContext<UserRoleContextType | undefined>(undefined);

const getUserRolesFromAccessToken = (token: string): String[] => {
  const payload = token.split('.')[1];
  const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  const roles = decoded.resource_access?.[import.meta.env.VITE_CLIENT_ID]?.roles;
  return roles;
};

export const UserRoleProvider = ({ children }: { children: ReactNode }) => {
  const auth = useAuth();
  const { slug } = useParams({ strict: false });

  const tokenUserRole = useMemo<UserRoleEnum | null>(() => {
    if (!auth.isAuthenticated || !auth.user) return null;

    const SSORoles = getUserRolesFromAccessToken(auth.user.access_token);

    if (SSORoles.includes('user_admin')) return UserRoleEnum.SUPER_ADMIN;
    if (SSORoles.includes('user_consultant')) return UserRoleEnum.CONSULTANT;
    if (SSORoles.includes('access')) return UserRoleEnum.BASE_USER;

    return null;
  }, [auth.isAuthenticated, auth.user]);

  // Fallback query if token doesn't provide specific role or only provides BASE_USER
  const { data: currentUser, isLoading } = useQuery({
    queryKey: ['userRoleFromApi'],
    queryFn: async () => await UsersService.readUserMe({ companySlug: slug }),
    enabled: !!auth.isAuthenticated && (!tokenUserRole || tokenUserRole === UserRoleEnum.BASE_USER),
  });

  const apiRole = currentUser?.role;

  const finalUserRole: UserRoleEnum | null = useMemo(() => {
    if (apiRole === 'admin') return UserRoleEnum.ADMIN;
    if (apiRole === 'editor') return UserRoleEnum.EDITOR;
    if (apiRole === 'collector') return UserRoleEnum.COLLECTOR;

    // If API didn't return a more specific role, fall back to token role
    return tokenUserRole;
  }, [apiRole, tokenUserRole]);

  const contextValue = useMemo(() => {
    return {
      userRole: finalUserRole,
      isAuthenticated: auth.isAuthenticated,
    };
  }, [tokenUserRole, apiRole, auth.isAuthenticated]);

  if (isLoading) return <Loader loadingText="Authentification en cours ..." />;
  return <UserRoleContext.Provider value={contextValue}>{children}</UserRoleContext.Provider>;
};

export const useUserRole = (): UserRoleContextType => {
  const context = useContext(UserRoleContext);
  if (!context) {
    throw new Error('useUserRole must be used within a UserRoleProvider');
  }
  return context;
};
